import { fetchAuthSession } from "aws-amplify/auth";
import {
  getSelectedAccountId,
  getSelectedAccountSummary,
} from "../../shared/lib/auth/accountContext";
import { getCachedUserAttributes } from "./authSessionAttributes";
import { isEmailRecentlyConfirmed } from "./emailVerificationOverride";

function toBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value == null) return fallback;
  return String(value).toLowerCase() === "true";
}

function getStoredSignInLoginId(username) {
  if (typeof window === "undefined") return null;

  try {
    const normalizedUsername = String(username || "").trim();
    const matchingSuffix = normalizedUsername
      ? `.${normalizedUsername}.signInDetails`
      : ".signInDetails";

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.endsWith(matchingSuffix)) continue;

      const parsed = JSON.parse(window.localStorage.getItem(key) || "null");
      const loginId = String(parsed?.loginId || "").trim();
      if (loginId) return loginId;
    }
  } catch {
    return null;
  }

  return null;
}

function buildUserFromTokenPayload(payload, attributes) {
  const loginId = getStoredSignInLoginId(payload["cognito:username"]);
  const email =
    attributes?.email ||
    payload.email ||
    (loginId?.includes("@") ? loginId : null);
  const selectedAccountId = getSelectedAccountId(email);
  const selectedAccountSummary = getSelectedAccountSummary(email);
  const selectedAccountPermissions =
    selectedAccountSummary?.permissions &&
    typeof selectedAccountSummary.permissions === "object"
      ? selectedAccountSummary.permissions
      : null;
  const selectedAccountUser =
    selectedAccountSummary?.user &&
    typeof selectedAccountSummary.user === "object"
      ? selectedAccountSummary.user
      : {};
  const accountsCount = Number(payload["custom:accounts_count"] || 0);
  const isProfileComplete = toBoolean(
    attributes?.["custom:complete_profile"],
    toBoolean(payload["custom:complete_profile"])
  );
  const tokenRequiresAccountSelection = toBoolean(
    payload["custom:account_selection_required"]
  );
  const accountId = selectedAccountId || null;
  const accountSelectionRequired = !accountId && tokenRequiresAccountSelection;

  return {
    id: payload.sub,
    username: payload["cognito:username"],
    email,
    loginId,
    emailVerified: toBoolean(
      attributes?.email_verified,
      toBoolean(payload.email_verified)
    ) || isEmailRecentlyConfirmed(email),
    completeProfile: isProfileComplete,
    name:
      selectedAccountUser.displayName ||
      attributes?.name ||
      payload.name ||
      null,
    nickname: selectedAccountUser.nickname || null,
    picture: selectedAccountUser.pictureUrl || payload.picture || null,
    picture_url: selectedAccountUser.pictureUrl || payload.picture || null,
    user_avatar_url: selectedAccountUser.pictureUrl || payload.picture || null,
    avatar_config: selectedAccountUser.avatarConfig || null,
    accountId,
    accountName: selectedAccountSummary?.name || null,
    accountStatus: selectedAccountSummary?.status || null,
    account_status: selectedAccountSummary?.status || null,
    accountLogoUrl: selectedAccountSummary?.logoUrl || null,
    accountSelectionRequired,
    accountsCount,
    is_account_owner: Boolean(selectedAccountSummary?.isAccountOwner),
    isAccountOwner: Boolean(selectedAccountSummary?.isAccountOwner),
    permissions: selectedAccountPermissions,
    permissionsLoaded: Boolean(
      selectedAccountSummary?.permissionsLoaded || selectedAccountPermissions
    ),
    permission_levels: selectedAccountPermissions?.permission_levels || null,
    permission_slugs: selectedAccountPermissions?.permission_slugs || null,
  };
}

export async function getAuthSessionUser({ forceRefresh = false } = {}) {
  const session = await fetchAuthSession({ forceRefresh });
  const idToken = session?.tokens?.idToken;

  if (!idToken) return null;

  let attributes = null;

  try {
    attributes = await getCachedUserAttributes();
  } catch {
    attributes = null;
  }

  return buildUserFromTokenPayload(idToken.payload, attributes);
}
